/// useEngineWorker: React hook for engine worker lifecycle management.
/// Provides typed API for deploy, start, activateJobs, completeJob, failJob
/// operations against BPMN or Node engine WASM workers.

import { useRef, useEffect, useCallback, useState } from "react";
import { getWorkerManager, type WorkerRole } from "@/lib/WorkerManager";
import type { MessageEnvelope } from "@lib/MessageEnvelope.js";
import type {
  DeployResponse,
  StartInstanceResponse,
  ActivateJobsResponse,
  CompleteJobResponse,
  FailJobResponse,
  InspectInstanceResponse,
} from "@/protocol";

/** Engine worker types that this hook manages. */
type EngineRole = "bpmn-engine" | "node-engine";

interface UseEngineWorkerOptions {
  /** Which engine to use. */
  role: EngineRole;
  /** Auto-start the worker on mount (default: false). */
  autoStart?: boolean;
  /** Request timeout in ms (default: 10000). */
  timeoutMs?: number;
}

interface UseEngineWorkerReturn {
  /** Whether the worker is ready to accept commands. */
  isReady: boolean;
  /** Any error from the worker. */
  error: string | null;
  /** Start (create) the worker. */
  start: () => void;
  /** Stop (terminate) the worker. */
  stop: () => void;
  /** Deploy a BPMN process definition. */
  deployBPMN: (bpmnXml: string, resourceName: string) => Promise<DeployResponse["payload"]>;
  /** Deploy a node flow definition. */
  deployNodeFlow: (
    nodeflowJson: string,
    resourceName: string,
  ) => Promise<DeployResponse["payload"]>;
  /** Start a new process instance. */
  startInstance: (
    processId: string,
    variables?: Record<string, unknown>,
  ) => Promise<StartInstanceResponse["payload"]>;
  /** Activate jobs of a given type. */
  activateJobs: (
    jobType: string,
    workerId: string,
    maxJobs?: number,
  ) => Promise<ActivateJobsResponse["payload"]>;
  /** Complete an activated job. */
  completeJob: (
    jobKey: string,
    variables?: Record<string, unknown>,
  ) => Promise<CompleteJobResponse["payload"]>;
  /** Fail an activated job. */
  failJob: (jobKey: string, errorMessage: string) => Promise<FailJobResponse["payload"]>;
  /** Inspect a process instance. */
  inspect: (instanceKey: string) => Promise<InspectInstanceResponse["payload"]>;
}

/**
 * React hook for managing an engine WASM worker lifecycle.
 * Creates a web worker, registers with WorkerManager, and provides
 * a typed API for engine commands.
 *
 * StrictMode-safe: uses deferred cleanup to survive React 18
 * StrictMode's mount-unmount-remount cycle without terminating
 * the worker prematurely.
 */
export function useEngineWorker(options: UseEngineWorkerOptions): UseEngineWorkerReturn {
  const { role, autoStart = false, timeoutMs = 10000 } = options;
  const workerRef = useRef<Worker | null>(null);
  const workerIdRef = useRef<string>(`${role}-${Date.now()}`);
  const cleanupRef = useRef<(() => void) | null>(null);
  const mountedRef = useRef(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createWorker = useCallback(() => {
    // If worker already exists, skip creation
    if (workerRef.current) return;

    const manager = getWorkerManager();
    const wid = workerIdRef.current;

    // Create the appropriate worker
    let worker: Worker;
    if (role === "bpmn-engine") {
      worker = new Worker(new URL("../workers/bpmn-engine.worker.ts", import.meta.url), {
        type: "module",
      });
    } else {
      worker = new Worker(new URL("../workers/node-engine.worker.ts", import.meta.url), {
        type: "module",
      });
    }

    workerRef.current = worker;

    worker.onerror = (ev) => {
      setError(String(ev.message ?? "Worker load error"));
    };

    manager.registerWorker({ workerId: wid, role: role as WorkerRole, worker });

    // Listen for ready/error events
    const unsubReady = manager.onEngineEvent("ready", (sourceId) => {
      if (sourceId === wid) {
        setIsReady(true);
        setError(null);
      }
    });
    const unsubError = manager.onEngineEvent("error", (sourceId, env) => {
      if (sourceId === wid) {
        setError(String(env.payload?.message ?? "Unknown error"));
      }
    });

    cleanupRef.current = () => {
      unsubReady();
      unsubError();
    };
  }, [role]);

  const start = useCallback(() => {
    if (workerRef.current) return; // Already running
    createWorker();
  }, [createWorker]);

  const stop = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    const manager = getWorkerManager();
    const wid = workerIdRef.current;
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    manager.unregisterWorker(wid);
    setIsReady(false);
    setError(null);
    // Generate new ID for next start
    workerIdRef.current = `${role}-${Date.now()}`;
  }, [role]);

  // StrictMode-safe auto-start and cleanup.
  // Uses a deferred check to distinguish StrictMode remount from real unmount.
  useEffect(() => {
    mountedRef.current = true;
    if (autoStart) {
      createWorker();
    }
    return () => {
      mountedRef.current = false;

      // Defer termination -- if StrictMode remounts the component,
      // mountedRef will be set back to true before this fires.
      const capturedWorker = workerRef.current;
      const capturedWid = workerIdRef.current;
      const capturedCleanup = cleanupRef.current;

      setTimeout(() => {
        if (mountedRef.current) return; // StrictMode remounted -- keep the worker alive

        // Real unmount -- terminate the worker
        capturedCleanup?.();
        if (capturedWorker) {
          capturedWorker.terminate();
        }
        if (workerRef.current === capturedWorker) {
          workerRef.current = null;
        }
        const manager = getWorkerManager();
        manager.unregisterWorker(capturedWid);
      }, 50);
    };
  }, [autoStart, createWorker]);

  // -------------------------------------------------------------------------
  // Typed command helpers
  // -------------------------------------------------------------------------

  const sendCommand = useCallback(
    async (type: string, payload: Record<string, unknown>): Promise<MessageEnvelope> => {
      const manager = getWorkerManager();
      return manager.sendCommand(
        workerIdRef.current,
        { type, direction: "cmd", payload } as never,
        timeoutMs,
      );
    },
    [timeoutMs],
  );

  const deployBPMN = useCallback(
    async (bpmnXml: string, resourceName: string) => {
      const resp = await sendCommand("deploy", { bpmn_xml: bpmnXml, resource_name: resourceName });
      return resp.payload as DeployResponse["payload"];
    },
    [sendCommand],
  );

  const deployNodeFlow = useCallback(
    async (nodeflowJson: string, resourceName: string) => {
      const resp = await sendCommand("deploy", {
        nodeflow_json: nodeflowJson,
        resource_name: resourceName,
      });
      return resp.payload as DeployResponse["payload"];
    },
    [sendCommand],
  );

  const startInstance = useCallback(
    async (processId: string, variables?: Record<string, unknown>) => {
      const payload: Record<string, unknown> = { process_key: processId };
      if (variables) {
        payload.variables = JSON.stringify(variables);
      }
      const resp = await sendCommand("start", payload);
      return resp.payload as StartInstanceResponse["payload"];
    },
    [sendCommand],
  );

  const activateJobs = useCallback(
    async (jobType: string, workerId: string, maxJobs = 5) => {
      const resp = await sendCommand("activateJobs", {
        job_type: jobType,
        worker_id: workerId,
        max_jobs: String(maxJobs),
      });
      return resp.payload as ActivateJobsResponse["payload"];
    },
    [sendCommand],
  );

  const completeJob = useCallback(
    async (jobKey: string, variables?: Record<string, unknown>) => {
      const resp = await sendCommand("completeJob", {
        job_key: jobKey,
        variables: JSON.stringify(variables ?? {}),
      });
      return resp.payload as CompleteJobResponse["payload"];
    },
    [sendCommand],
  );

  const failJob = useCallback(
    async (jobKey: string, errorMessage: string) => {
      const resp = await sendCommand("failJob", {
        job_key: jobKey,
        error_message: errorMessage,
      });
      return resp.payload as FailJobResponse["payload"];
    },
    [sendCommand],
  );

  const inspect = useCallback(
    async (instanceKey: string) => {
      const resp = await sendCommand("inspect", { instance_key: instanceKey });
      return resp.payload as InspectInstanceResponse["payload"];
    },
    [sendCommand],
  );

  return {
    isReady,
    error,
    start,
    stop,
    deployBPMN,
    deployNodeFlow,
    startInstance,
    activateJobs,
    completeJob,
    failJob,
    inspect,
  };
}
